import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { login, logoutRequest, refreshSession, register, updateProfile } from '../api/authClient';
import { clearCache, clearSession, readSession, writeSession } from '../offline/cache';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    refreshSession().then((session) => {
      if (cancelled) return;
      if (session?.error === 'network') {
        const cached = readSession();
        if (cached) {
          setUser(cached);
          setNotice('');
          setStatus('authenticated');
          return;
        }
        setNotice('network');
        setStatus('anonymous');
        return;
      }
      if (session?.user) {
        writeSession(session.user);
        setUser(session.user);
        setStatus('authenticated');
        return;
      }
      clearSession();
      setStatus('anonymous');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const result = await login(email, password);
    if (result.ok) {
      writeSession(result.body.user);
      setUser(result.body.user);
      setNotice('');
      setStatus('authenticated');
    }
    return result;
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    const result = await register(email, password, name);
    if (result.ok) {
      writeSession(result.body.user);
      setUser(result.body.user);
      setNotice('');
      setStatus('authenticated');
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    const id = user?.id;
    await logoutRequest();
    clearSession();
    if (id) await clearCache(id);
    setUser(null);
    setStatus('anonymous');
  }, [user]);

  const updateName = useCallback(async (name) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 80) return false;
    const result = await updateProfile(trimmed);
    if (!result.ok || !result.body.user) return false;
    writeSession(result.body.user);
    setUser(result.body.user);
    return true;
  }, []);

  const value = useMemo(
    () => ({ status, user, notice, signIn, signUp, logout, updateName }),
    [status, user, notice, signIn, signUp, logout, updateName]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
